
import {OrderLifetime, OrderSide} from "../../../../../orders/models/OrderRecord";
import {QuoteViewModel} from "../../../../../quote/models/QuoteViewModel";
import {Exchange} from "../../../../../instruments/exchange/Exchange";
import {AccountBriefRecord} from "../../../../../account/types/AccountBriefRecord";
import {PricingInstruction} from "../../../../../orders/models/PricingInstruction";
import {PriceStrategy} from "../../../../../components/inputs/price/input/PriceStrategy";
import {OrderAdvice} from "../../place-order-wizard/models/OrderAdvice";

export interface MultiAccountEquityWizardData {
    readonly current: SingleAccountEquityWizardData;
    readonly orders: Array<SingleAccountEquityWizardData>;
    readonly advice?: OrderAdvice;
}

export interface SingleAccountEquityWizardData {
    readonly code?: string;
    readonly name?: string;
    readonly exchange?: Exchange;
    readonly side: OrderSide;
    readonly quantity: number;
    readonly limit?: number;
    readonly lifetime: OrderLifetime;
    readonly expiry?: Date;
    readonly pricingInstruction: PricingInstruction;
    readonly type: EquityOrderType;
    readonly totalValue: number;
    readonly portfolio?: AccountBriefRecord;
    readonly quote?: QuoteViewModel;
    readonly strategy?: PriceStrategy;
    readonly isStopLoss: boolean;
    readonly stopLossLimit?: number | null;
}

export enum EquityOrderType {
    QUANTITY = "QUANTITY",
    TOTAL_VALUE = "TOTAL_VALUE"
}

interface Primitive {
    [key: string]: string | number | boolean;
}

export function isSameAccountBriefRecord(data1: AccountBriefRecord, data2: AccountBriefRecord): boolean {
    // Compare primitive properties
    return (
        data1.id === data2.id &&
        data1.number === data2.number &&
        data1.name === data2.name &&
        data1.portfolioType === data2.portfolioType &&
        data1.ownerId === data2.ownerId &&
        // Assuming IsoDateTime has a suitable comparison method (replace if needed)
        data1.created === data2.created
    );
}

export function isSameSingleAccountEquityData(data1: SingleAccountEquityWizardData, data2: SingleAccountEquityWizardData): boolean {
    // Compare primitive properties
    if (
        data1.code !== data2.code ||
        data1.name !== data2.name ||
        data1.exchange !== data2.exchange ||
        data1.side !== data2.side ||
        data1.quantity !== data2.quantity ||
        data1.limit !== data2.limit ||
        data1.lifetime !== data2.lifetime ||
        data1.pricingInstruction !== data2.pricingInstruction ||
        data1.type !== data2.type ||
        data1.totalValue !== data2.totalValue
    ) {
        return false;
    }

    // Handle optional expiry dates
    if (data1.expiry && data2.expiry) {
        if (data1.expiry.getTime() !== data2.expiry.getTime()) {
            return false;
        }
    }

    // Handle optional portfolio objects (assuming deep comparison needed)
    if (data1.portfolio && data2.portfolio) {
        if (!isSameAccountBriefRecord(data1.portfolio, data2.portfolio)) {
            return false;
        }
    } else if (data1.portfolio || data2.portfolio) {
        // One has portfolio and the other doesn't, so they are not equal
        return false;
    }

    // All properties match
    return true;
}

export function isSameMultiAccountWizardData<T extends object | Primitive>(obj1: MultiAccountEquityWizardData, obj2: MultiAccountEquityWizardData): boolean {
    // Check for basic equality
    if (obj1 === obj2) {
        return true;
    }

    // Check for null or undefined
    if (obj1 == null || obj2 == null) {
        return false;
    }

    // Check for different types
    if (typeof obj1 !== typeof obj2) {
        return false;
    }

    // Handle primitive types (numbers, strings, booleans)
    if (isPrimitive(obj1) && isPrimitive(obj2)) {
        return obj1 === obj2;
    }

    // Handle arrays
    if (Array.isArray(obj1) && Array.isArray(obj2)) {
        if (obj1.length !== obj2.length) {
            return false;
        }
        for (let i = 0; i < obj1.length; i++) {
            if (!isSameMultiAccountWizardData(obj1[i], obj2[i])) {
                return false;
            }
        }
        return true;
    }

    if (obj1.orders && obj2.orders) {
        // Check for the same number of keys
        if (obj1.orders.length !== obj2.orders.length) {
            return false;
        }

        for (let i = 0; i < obj1.orders.length; i++) {
            if (!isSameSingleAccountEquityData(obj1.orders[i], obj2.orders[i])) {
                return false;
            }
        }
    }

    return true;
}

// Helper function to check for primitive types
function isPrimitive(value: any): value is (string | number | boolean) {
    return typeof value === "string" || typeof value === "number" || typeof value === "boolean";
}
